// Oyuncu portalında uygulanan tarayıcı kısıtları: sağ tık menüsü, metin
// kopyalama ve geliştirici araçları kısayolları.
//
// Üçü de panelden ayrı ayrı açılıp kapatılır (Ayarlar → Site Ayarları →
// Oyuncu portalı tarayıcı kısıtları). Hiçbiri **güvenlik önlemi değildir** —
// kaynak kodu tarayıcının
// kendi menüsünden yine görülür, geliştirici araçları menüden açılır; amaç
// sıradan kopyalamayı zorlaştırmaktır. Bu yüzden kısıtlar hiçbir zaman
// sunucu tarafındaki bir kontrolün yerine geçmez.
//
// Kısıtlar yalnızca oyuncu portalında çalışır; yönetim panelinde
// uygulanmazlar (bkz. components/BrowserProtection.jsx).

// Metin seçimi kapalıyken bile seçilebilir kalan düğümler. Form alanları
// olmadan oyuncu yazdığını düzeltemez; `data-selectable` ise IBAN, kripto
// adresi gibi elle kopyalanması gereken değerler içindir — kopyalayamayan
// oyuncu para yatıramaz.
const SELECTABLE_SELECTOR = [
  'input',
  'textarea',
  'select',
  '[contenteditable]:not([contenteditable="false"])',
  '[data-selectable="true"]',
].join(',');

const NO_SELECT_CLASS = 'portal-no-select';
const NO_CALLOUT_CLASS = 'portal-no-callout';

const INSPECTOR_KEYS = new Set(['c', 'e', 'i', 'j', 'k']);

export const isAdminPath = (pathname) => (
  pathname === '/admin' || String(pathname || '').startsWith('/admin/')
);

export const isBlockedDeveloperShortcut = (event) => {
  const key = String(event?.key || '').toLowerCase();

  if (key === 'f12') return true;

  const commandKey = Boolean(event?.ctrlKey || event?.metaKey);
  if (commandKey && key === 'u') return true;
  if (commandKey && event?.shiftKey && INSPECTOR_KEYS.has(key)) return true;

  // Safari/Chrome on macOS use Cmd+Option+I/J/C for developer tools.
  return Boolean(event?.metaKey && event?.altKey && ['c', 'i', 'j', 'u'].includes(key));
};

// Olayın hedefi muaf bir alanın içinde mi? Kısıt yalnızca sayfanın içeriğine
// uygulanır, oyuncunun kendi yazdığı metne değil.
export const isSelectableTarget = (node) => {
  const element = node?.nodeType === 3 ? node.parentElement : node;
  if (!element || typeof element.closest !== 'function') return false;
  return Boolean(element.closest(SELECTABLE_SELECTOR));
};

/**
 * Seçilen kısıtları belgeye bağlar ve sökme fonksiyonunu döndürür.
 *
 * @param {{contextMenu?: boolean, textCopy?: boolean, devtools?: boolean}} options
 * @param {Document} target
 */
export const installBrowserProtections = (options = {}, target = document) => {
  const contextMenu = options.contextMenu === true;
  const textCopy = options.textCopy === true;
  const devtools = options.devtools === true;

  const root = target?.documentElement;
  const cleanups = [];

  if (contextMenu) {
    const preventContextMenu = (event) => {
      // Form alanlarında menü açık kalır: yapıştırma ve yazım denetimi
      // oyuncunun kendi metnine aittir.
      if (isSelectableTarget(event.target)) return;
      event.preventDefault();
    };
    const preventRightButton = (event) => {
      if (event.button !== 2) return;
      if (isSelectableTarget(event.target)) return;
      event.preventDefault();
    };

    target.addEventListener('contextmenu', preventContextMenu, true);
    target.addEventListener('pointerdown', preventRightButton, true);
    // iOS'ta bağlantıya uzun basınca çıkan önizleme menüsü CSS ile kapanır.
    root?.classList.add(NO_CALLOUT_CLASS);
    cleanups.push(() => {
      target.removeEventListener('contextmenu', preventContextMenu, true);
      target.removeEventListener('pointerdown', preventRightButton, true);
      root?.classList.remove(NO_CALLOUT_CLASS);
    });
  }

  if (textCopy) {
    const preventCopy = (event) => {
      // Metin seçimi document/body üzerinde başlayabilir; gerçek seçimin
      // odağı muaf bir ödeme/referans alanındaysa onu da serbest bırak.
      const selectionTarget = target.getSelection?.()?.anchorNode;
      if (
        isSelectableTarget(event.target)
        || isSelectableTarget(selectionTarget)
      ) return;
      event.preventDefault();
    };

    target.addEventListener('copy', preventCopy, true);
    target.addEventListener('cut', preventCopy, true);
    // Seçimin kendisi CSS ile kapatılır; olay engeli Ctrl+C ve tarayıcının
    // kopyalama komutunu kapsar. Uygulamanın açıkça sunduğu Kopyala düğmeleri
    // Clipboard API ile çalışmaya devam eder.
    root?.classList.add(NO_SELECT_CLASS);
    cleanups.push(() => {
      target.removeEventListener('copy', preventCopy, true);
      target.removeEventListener('cut', preventCopy, true);
      root?.classList.remove(NO_SELECT_CLASS);
    });
  }

  if (devtools) {
    const preventShortcut = (event) => {
      if (!isBlockedDeveloperShortcut(event)) return;
      event.preventDefault();
      event.stopPropagation();
    };

    target.addEventListener('keydown', preventShortcut, true);
    cleanups.push(() => {
      target.removeEventListener('keydown', preventShortcut, true);
    });
  }

  return () => cleanups.forEach((cleanup) => cleanup());
};
